import { useState } from 'react';

import Button from '@components/atoms/button';
import FFHModal from '@components/modal';
import {
  BodyRegular,
  BodySmallRegular,
  H2
} from '@components/atoms/typography';

const RESET_FAILED_MESSAGE =
  "We couldn't reset your team just now. Please try again in a few minutes.";

const MODAL_TITLE = 'Reset your team';

type ResetTeamModalProps = {
  isOpen: boolean;
  onClose: () => void;
  /** Re-imports the squad using the FPL ID already on the profile. */
  onReset: () => Promise<void>;
  isResetting: boolean;
  /** Secondary path: switch to importing a different FPL team. */
  onSwitchTeam: () => void;
};

/**
 * The primary reset action restores the squad to its gameweek-start state by
 * re-importing from FPL with the ID already stored on the user's profile, so
 * no ID entry is needed. Importing a different team is the secondary path,
 * handed off to the team import modal.
 */
export const ResetTeamModal = ({
  isOpen,
  onClose,
  onReset,
  isResetting,
  onSwitchTeam
}: ResetTeamModalProps) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleClose = () => {
    if (isResetting) return;
    setErrorMessage(null);
    onClose();
  };

  const handleReset = async () => {
    setErrorMessage(null);
    try {
      await onReset();
      onClose();
    } catch {
      // Keep the modal open so the user can retry.
      setErrorMessage(RESET_FAILED_MESSAGE);
    }
  };

  // Clear any failed-reset message before switching modals, otherwise it
  // lingers in state and reappears when the reset modal is next opened.
  const handleSwitchTeam = () => {
    setErrorMessage(null);
    onSwitchTeam();
  };

  return (
    <FFHModal open={isOpen} handleClose={handleClose} title={MODAL_TITLE}>
      <div
        className="flex flex-col gap-4 p-6 max-w-[500px]"
        data-cy="reset-team-modal"
      >
        <H2>{MODAL_TITLE}</H2>
        <BodyRegular textColor="text-neutral-700">
          This resets your team and plans back to the players you had at the
          start of the gameweek. If you want a different team, you can{' '}
          <button
            type="button"
            className="font-bold underline"
            onClick={handleSwitchTeam}
            disabled={isResetting}
            data-cy="reset-team-switch"
          >
            import a different team
          </button>{' '}
          instead.
        </BodyRegular>

        {errorMessage && (
          <BodySmallRegular
            textColor="text-danger-700"
            data-cy="reset-team-error"
          >
            {errorMessage}
          </BodySmallRegular>
        )}

        <Button
          variant="danger"
          onClick={handleReset}
          disabled={isResetting}
          dataCy="reset-team-confirm"
        >
          {isResetting ? 'Resetting...' : 'Reset to GW start'}
        </Button>
      </div>
    </FFHModal>
  );
};
